/**
 * Capturar Frame — la captura la hace EL NAVEGADOR (video → canvas → PNG).
 * Nada de esto toca el servidor ni gasta créditos.
 *
 * `captureFrame(nodeId, timeSeconds)` es la misma operación que el botón del
 * nodo: la puede llamar Lumière u otro código de la UI.
 */
import { useWorkflowStore } from '../store/workflowStore';
import { uploadToR2 } from './uploadToR2';
import { proxyUrlFor } from '../services/templateService';

export const CAPTURE_FRAME_ENABLED =
  String(process.env.REACT_APP_CAPTURE_FRAME_NODE ?? 'true').toLowerCase() !== 'false';

const DEFAULT_FPS = 30;           // paso de cuadro cuando no conocemos el fps real
const SEEK_TIMEOUT_MS = 15000;

const makeVideo = (url) => {
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.src = url;
  video.load();
  return video;
};

const once = (video, event, timeoutMs) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`timeout esperando ${event}`));
    }, timeoutMs);
    const onOk = () => { cleanup(); resolve(); };
    const onErr = () => { cleanup(); reject(new Error(`error cargando el video (${event})`)); };
    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener(event, onOk);
      video.removeEventListener('error', onErr);
    };
    video.addEventListener(event, onOk, { once: true });
    video.addEventListener('error', onErr, { once: true });
  });

/** Busca el segundo exacto y espera a que el cuadro esté pintado. */
const seekAndPaint = async (video, time) => {
  const painted = video.requestVideoFrameCallback
    ? new Promise((resolve) => video.requestVideoFrameCallback(() => resolve()))
    : new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  video.currentTime = time;
  await once(video, 'seeked', SEEK_TIMEOUT_MS);
  await painted;
};

const release = (video) => {
  try {
    video.removeAttribute('src');
    video.load();
  } catch { /* liberar memoria del decoder: best-effort */ }
};

/**
 * Intenta capturar desde una URL. Lanza si el video no carga o si el canvas
 * queda contaminado por CORS (SecurityError en drawImage/toBlob).
 */
const captureFrom = async (url, timeSeconds) => {
  const video = makeVideo(url);
  try {
    await once(video, 'loadedmetadata', SEEK_TIMEOUT_MS);
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    // Si piden más allá del final, capturamos el último cuadro y avisamos.
    const clamped = timeSeconds > duration;
    const target = clamped ? Math.max(0, duration - 1 / DEFAULT_FPS)
                           : Math.max(0, timeSeconds);
    await seekAndPaint(video, target);

    const width = video.videoWidth;   // resolución NATIVA del archivo,
    const height = video.videoHeight; // nunca el tamaño en pantalla
    if (!width || !height) throw new Error('el video no tiene pista de imagen');

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas vacío'))),
                    'image/png'));
    return { blob, width, height, timeUsed: target, duration, clamped };
  } finally {
    release(video);
  }
};

/**
 * Captura un cuadro de un video por URL. Primero directo; si el video no se
 * puede leer (CORS), repite por el proxy propio — el mismo respaldo que usan
 * las miniaturas de plantillas.
 */
export const captureFrameToBlob = async (videoUrl, timeSeconds) => {
  try {
    return await captureFrom(videoUrl, timeSeconds);
  } catch (directError) {
    try {
      return await captureFrom(proxyUrlFor(videoUrl), timeSeconds);
    } catch (proxyError) {
      const corsish = /SecurityError|CORS|error cargando/i.test(String(directError));
      throw new Error(
        corsish
          ? 'No se pudo leer el video (CORS). Ni directo ni por el proxy: revisá que la URL sea accesible.'
          : `No se pudo capturar el cuadro: ${proxyError.message || proxyError}`);
    }
  }
};

/**
 * Disparo programático — hace EXACTAMENTE lo mismo que el botón "Capturar"
 * del nodo. Lumière la usa para elegir el segundo, capturar y mostrar el
 * resultado al usuario sin que toque nada.
 *
 * Devuelve { assetId, outputUrl, width, height, timeSeconds, clamped }.
 */
export const captureFrame = async (nodeId, timeSeconds) => {
  if (!CAPTURE_FRAME_ENABLED) {
    throw new Error('El nodo Capturar Frame está apagado (REACT_APP_CAPTURE_FRAME_NODE=false)');
  }
  const store = useWorkflowStore.getState();
  const node = store.nodes.find((n) => n.id === nodeId);
  if (!node) throw new Error(`nodo no encontrado: ${nodeId}`);

  const input = store.getInputForNode(nodeId);
  const videoUrl = input?.outputUrl;
  if (!videoUrl) throw new Error('El nodo no tiene un video conectado (o todavía no cargó)');

  const { blob, width, height, timeUsed, clamped } =
    await captureFrameToBlob(videoUrl, Number(timeSeconds) || 0);

  const file = new File([blob], `capture_${nodeId}_${timeUsed.toFixed(2)}s.png`,
                        { type: 'image/png' });
  // El mismo endpoint de "Cargar Imagen", marcado como salida del nodo.
  const up = await uploadToR2(file, { role: 'output', origin: 'capture_frame' });

  store.updateNodeData(nodeId, {
    outputUrl: up.url,
    assetId: up.assetId || null,
    width,
    height,
    sourceVideoUrl: videoUrl,
    timeSeconds: Number(timeSeconds) || 0,
    capturedTimeSeconds: timeUsed,
    capturedAt: new Date().toISOString(),
    hasOutput: true,
    outputType: 'image',
  });
  return { assetId: up.assetId || null, outputUrl: up.url, width, height,
           timeSeconds: timeUsed, clamped };
};

export default captureFrame;
